// CapabilitySummary — the read-only, GRANTED-ONLY view of what a grant set can do,
// grouped by section. The complement of CapabilityEditor: the editor is the
// interactive EDIT surface (all families, controls, hints); this is the compact
// VIEW (only what's granted, no controls, no edit-noise). Used wherever a grant set
// is shown read-only — a role's detail body, the role peek (its own + the users-list
// role cell), the create wizard's review step, an application credential's Access
// card — so a role's card and a client's card read identically. Pure + data-in: no
// fetching, no peek/page coupling, so it drops into any container.
//
// Off the same catalogue + presentation map as the editor, so the two can't drift.
// A key that carries a narrowing list (managed roles · the level-set · device-target
// kinds) gets its detail line from the consumer's `narrowingSummary` slot.

import { useTranslation } from 'react-i18next';
import type { PermissionKey } from '@ems-app/schemas/permissions';
import { SECTIONS, type Family } from './presentation.js';
import { icons } from '../../ui/icons.js';

export interface CapabilitySummaryProps {
  grants: string[];
  /** The narrowing-list detail for a granted key ("Delegated roles: A, B" · the
   *  level names · the device-target kinds) — the consumer's instance; return
   *  null / undefined where the key carries none. */
  narrowingSummary?: (key: PermissionKey) => string | null | undefined;
  /** Shown when nothing is granted (a consumer-worded reason where one exists —
   *  a role driven by job assignment, a client that never acts for a person). */
  emptyMessage?: string;
  className?: string;
}

export function CapabilitySummary({ grants, narrowingSummary, emptyMessage, className = '' }: CapabilitySummaryProps) {
  const { t } = useTranslation('permissions');
  const grantSet = new Set(grants);

  // The granted capability bits for one family (empty ⇒ nothing granted here).
  const familyBits = (fam: Family): string[] => {
    const famLabel = t(fam.label);
    const bits: string[] = [];
    for (const th of fam.things ?? []) {
      if (th.kind === 'scale') {
        const rung = [...th.rungs].reverse().find((r) => grantSet.has(r.key));
        if (rung) {
          // Prefix the scale name only when it differs from the family (e.g. the
          // Assets family's "Financials" scale); the primary scale needs no prefix.
          const scaleLabel = t(th.label);
          bits.push(scaleLabel === famLabel ? t(rung.label) : `${scaleLabel}: ${t(rung.label)}`);
        }
        for (const a of th.adjuncts ?? []) {
          if (!grantSet.has(a.key)) continue;
          bits.push(t(a.label));
          const extra = narrowingSummary?.(a.key);
          if (extra) bits.push(extra);
        }
      } else if (grantSet.has(th.key)) {
        // A toggle family with one toggle carries no extra bit — the family line
        // itself IS the grant; several toggles (the machine contracts) name each.
        if ((fam.things ?? []).length > 1) bits.push(t(th.label));
        const extra = narrowingSummary?.(th.key);
        if (extra) bits.push(extra);
      }
    }
    return bits;
  };

  const sections = SECTIONS.map((sec) => ({
    id: sec.id,
    label: t(sec.label),
    families: sec.families
      .map((fam) => ({ fam, bits: familyBits(fam) }))
      // A family is shown iff it grants something: a toggle family with no narrowing
      // still grants (the toggle is on) so show it; scale families show iff bits.
      .filter(({ fam, bits }) => bits.length > 0 || (fam.things ?? []).some((th) => th.kind === 'toggle' && grantSet.has(th.key))),
  })).filter((s) => s.families.length > 0);

  if (sections.length === 0) {
    return (
      <p className={`text-sm text-[var(--color-text-secondary)] ${className}`}>
        {emptyMessage ?? t('summary.empty')}
      </p>
    );
  }

  // `@container`: the family row goes side-by-side (label │ bits) in a wide column
  // (the detail) and STACKS (label, then bits indented under it) in a narrow one
  // (the 384px peek) — one component, intrinsic to its container, no breakpoints.
  return (
    <div className={`@container flex flex-col gap-5 ${className}`}>
      {sections.map((sec) => (
        <section key={sec.id}>
          <h3 className="mb-2 px-1 text-xs font-medium uppercase tracking-wide text-[var(--color-text-secondary)]">{sec.label}</h3>
          <ul className="flex flex-col gap-2.5 @md:gap-2">
            {sec.families.map(({ fam, bits }) => {
              const Glyph = icons[fam.icon];
              return (
                <li key={fam.id} className="flex flex-col gap-0.5 text-sm @md:flex-row @md:items-baseline @md:gap-2.5">
                  <span className="flex items-baseline gap-2.5 @md:w-40 @md:shrink-0">
                    <Glyph size={16} aria-hidden className="relative top-0.5 shrink-0 text-[var(--color-text-secondary)]" />
                    <span className="font-medium text-[var(--color-text-primary)]">{t(fam.label)}</span>
                  </span>
                  {bits.length > 0 && (
                    <span className="min-w-0 text-[var(--color-text-secondary)] ps-[1.625rem] @md:ps-0">{bits.join(' · ')}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
